import React from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';

import {
    Button,
    IconAngleLeft,
    IconEllipses2,
    // IconFilePlus,
    IconRepeat,
    leftNavConstants,
    TrackStatusIcon,
    Tooltip,
} from '@trqk/components';
import { removeNulls, getProWorkIds } from '@trqk/utils';

import { AudioUploadOrPlayer, Ownership } from '..';
import { usePaymentPlanStatus } from '../../../../../hooks';

import './work-detail-header.css';

const { WIDTHS } = leftNavConstants;

const UPDATE_TRQK_AUDIO = gql`
    mutation updateTrqkAudio($input: UpdateTrqkAudioInput!) {
        updateTrqkAudio(input: $input) {
            id
            song_id
        }
    }
`;

interface WorkDetailHeaderProps {
    /**
     * Whether detection is enabled for this track
     */
    detectionIsOn: boolean;
    /**
     * The current entity
     */
    entity: TrqkEntity | null;
    /**
     * Method called when a new trqkAudio is created
     */
    onCreateTrqkAudio(trqkAudio: TrqkAudio | null): void;
    /**
     * Method called when a trqkAudio is updated to unmatch the track by providing a input with
     * its `song_id` set to `null`.
     */
    onUnmatchWork(): void;
    /**
     * If there is a trqkSong, this is the result of calling `getMergedTrackData` for all of the
     * trqkSong's statements
     */
    trackData: ProTrack | null;
    /**
     * The TrqkAudio, if there is one
     */
    trqkAudio: TrqkAudio | null;
    /**
     * TheTrqkSong, if there is one
     */
    trqkSong: TrqkSong | null;
}

/**
 * Renders header information for a WorkDetail page.
 * @param props - see {@link WorkDetailHeaderProps}
 */
export const WorkDetailHeader = ({
    detectionIsOn,
    entity,
    onCreateTrqkAudio,
    onUnmatchWork,
    trackData,
    trqkAudio,
    trqkSong,
}: WorkDetailHeaderProps): JSX.Element => {
    const { isPublisherEdition } = usePaymentPlanStatus();
    const [unmatchTrack] = useMutation<{
        updateTrqkAudio: { id: string; song_id: Maybe<string> };
    }>(UPDATE_TRQK_AUDIO, {
        onCompleted: (data): void => {
            const {
                updateTrqkAudio: { id },
            } = data;

            if (id) {
                onUnmatchWork();
            }
        },
    });
    const assetUrl = trqkAudio?.asset_url ?? null;
    const audioDeleted = trqkAudio?.deleted ?? false;
    const filename = trqkAudio?.filename ?? undefined;
    const title = trqkSong?.title ?? trqkAudio?.filename ?? 'Untitled Track';
    const percent_shares = trqkSong?.percent_shares ?? null;
    const workIds = removeNulls(trackData?.work_ids);
    const proWorkIds = getProWorkIds(workIds);
    const history = useHistory();
    const trqkSongId = trqkSong?.id ?? null;

    return (
        <div
            className="work-detail-header flex flex-col w-screen"
            style={{
                paddingTop: WIDTHS.MAIN_TOP_PADDING_SM,
            }}
        >
            <div style={{ width: WIDTHS.MAIN_XL }}>
                <button
                    type="button"
                    onClick={(): void => {
                        history.goBack();
                    }}
                >
                    <div className="flex items-center mb-12">
                        <IconAngleLeft width={16} fillColor="tertiary-300" />
                        <span className="ml-1 text-sm font-semibold text-tertiary-300 tracking-wide no-underline">
                            Back
                        </span>
                    </div>
                </button>
            </div>
            <div style={{ width: WIDTHS.MAIN_XL }}>
                {proWorkIds.length > 0 && (
                    <div className="text-tertiary-300 text-xs font-semibold tracking-widest mb-3">
                        {entity?.pro && (
                            <span className="text-secondary-500">
                                {entity.pro}
                            </span>
                        )}
                        &nbsp;ID# {proWorkIds.join(', ')}
                    </div>
                )}
                <div className="flex justify-start items-center">
                    <TrackStatusIcon
                        audioDeleted={audioDeleted}
                        title={trqkSong?.title}
                        filename={trqkAudio?.filename}
                    />
                    <h3 className="ml-2">{title}</h3>

                    {trqkAudio !== null && trqkSong !== null && (
                        <Tooltip
                            position="right"
                            background="light"
                            message={
                                <div className="w-3xl flex text-sm mx-2 my-3">
                                    <IconRepeat
                                        fillColor="primary-500"
                                        width={20}
                                    />
                                    <div className="flex-grow ml-2 mr-4 flex flex-col">
                                        <Button
                                            autoWidth={true}
                                            color="danger-tertiary-light-bg"
                                            padding="none"
                                            addClasses="mb-3"
                                            handleClick={(): void => {
                                                unmatchTrack({
                                                    variables: {
                                                        input: {
                                                            id: trqkAudio.id,
                                                            song_id: null,
                                                        },
                                                    },
                                                });
                                            }}
                                        >
                                            Unmatch Track
                                        </Button>
                                        <div className="flex-grow w-full clamp-3-lines">
                                            {filename}
                                        </div>
                                    </div>
                                </div>
                            }
                            trigger="click"
                            maxWidth={500}
                            minWidth={250}
                        >
                            <div className="ml-4 mr-2 rounded-full border border-tertiary-200 h-8 w-8 flex justify-center items-center text-middle">
                                <IconEllipses2 fillColor="tertiary-300" />
                            </div>
                        </Tooltip>
                    )}
                </div>
            </div>
            {!isPublisherEdition && (
                <div style={{ width: WIDTHS.MAIN_XL }}>
                    <AudioUploadOrPlayer
                        assetUrl={assetUrl}
                        detectionIsOn={detectionIsOn}
                        onCreateTrqkAudio={onCreateTrqkAudio}
                        percentShares={trqkSong?.percent_shares ?? null}
                        trqkSongId={trqkSong?.id ?? null}
                        title={title}
                        workIds={removeNulls<string>(
                            trqkSong?.work_ids ?? null,
                        )}
                    />
                    {percent_shares !== null &&
                        entity !== null &&
                        trqkSongId !== null && (
                            <Ownership
                                percent_shares={percent_shares}
                                entity={entity}
                                title={title}
                                trqkSongId={trqkSongId}
                            />
                        )}
                </div>
            )}
        </div>
    );
};
